import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

interface RetryableConfig extends InternalAxiosRequestConfig {
  _isRetry?: boolean;
}

/**
 * Access token хранится в memory (не localStorage) — защита от XSS.
 * Refresh token — httpOnly cookie, отправляется автоматически.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

const client = axios.create({
  baseURL: '/api',
  withCredentials: true, // httpOnly cookie для refresh
});

// Добавляем access token к запросам
client.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Auto-refresh при 401
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (config: InternalAxiosRequestConfig) => void;
  reject: (error: AxiosError) => void;
  config: InternalAxiosRequestConfig;
}> = [];

/**
 * Обновить токен, с ОДНОЙ повторной попыткой через паузу.
 *
 * Заведено 10.09 по жалобе «не авторизует». Refresh одноразовый с ротацией:
 * если обновление ушло из другой вкладки (или из запроса, задержавшегося в
 * полёте), наш токен к моменту отправки уже старый и получает отказ — при
 * живой сессии. Пауза даёт браузеру принять новую cookie, выданную тем
 * обновлением, и вторая попытка проходит.
 *
 * Одна попытка, не цикл: если сессии действительно нет, второй отказ это
 * подтвердит, и человек уйдёт на вход, а не будет крутиться.
 */
async function обновитьТокен(): Promise<{ data: { accessToken: string } }> {
  try {
    return await axios.post<{ accessToken: string }>(
      '/api/auth/refresh',
      {},
      { withCredentials: true }
    );
  } catch (первый) {
    const статус = (первый as AxiosError).response?.status;
    // Повторяем только отказ авторизации: 500 или обрыв сети повтором не
    // лечатся, а лишний запрос в такой момент только мешает
    if (статус !== 401) throw первый;
    await new Promise((r) => setTimeout(r, 400));
    return await axios.post<{ accessToken: string }>(
      '/api/auth/refresh',
      {},
      { withCredentials: true }
    );
  }
}

function processQueue(newToken: string | null): void {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (newToken) {
      config.headers.Authorization = `Bearer ${newToken}`;
      resolve(config);
    } else {
      reject(new axios.AxiosError('Не удалось обновить токен'));
    }
  });
  failedQueue = [];
}

client.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const retryable = error.config as RetryableConfig | undefined;

    // Не пытаемся refresh: нет config, не 401, login/refresh endpoint, или уже retry
    const skipRefreshUrls = [
      '/auth/login',
      '/auth/refresh',
      '/auth/accept-invite',
      '/auth/logout',
      // Обмен ссылки из телеграма — сам по себе вход: обновлять по нему
      // нечего, сессии ещё нет
      '/auth/telegram/exchange',
    ];
    if (
      !retryable ||
      error.response?.status !== 401 ||
      skipRefreshUrls.some(u => retryable.url?.startsWith(u)) ||
      retryable._isRetry
    ) {
      return Promise.reject(error);
    }

    retryable._isRetry = true;

    // Если уже refreshим — ставим в очередь
    if (isRefreshing) {
      return new Promise<InternalAxiosRequestConfig>((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: retryable });
      }).then((config) => client(config));
    }

    isRefreshing = true;

    try {
      const { data } = await обновитьТокен();

      setAccessToken(data.accessToken);
      processQueue(data.accessToken);

      // Повторяем оригинальный запрос
      retryable.headers.Authorization = `Bearer ${data.accessToken}`;
      return client(retryable);
    } catch {
      // Refresh не удался — разлогиниваем
      setAccessToken(null);
      processQueue(null);

      // Страницы, на которых вход как раз и происходит: перезагрузка отсюда
      // обрывает его на полпути. Ровно это и ломало вход по ссылке из бота —
      // проверка сессии на /auth/tg получала 401 и уводила на /login прежде,
      // чем страница успевала обменять токен
      const authPages = ['/login', '/auth/tg'];
      if (!authPages.includes(window.location.pathname)) {
        window.location.href = '/login';
      }

      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

export { client };
